/**
 * @file DocumentSelection.jsx
 * @author Rishikesh
 * @date 2024-09-18
 * @description Component of master settings to manage documents
 */
import { ContentPasteSearchOutlined } from "@mui/icons-material";
import {
  Box,
  Checkbox,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React from "react";

export const DocumentSelection = ({
  isLoading,
  value,
  documents,
  selectedCountry,
  selectedCourse,
  newDocument,
  setNewDocument,
}) => {
  const [searchQuery, setSearchQuery] = React.useState("");

  const filteredDocuments = documents.filter((item) =>
    item.documentname.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const isChecked = (document) => {
    const existingDocumentSet = newDocument.find(
      (c) =>
        c.documentset === value &&
        c.courseid === selectedCourse?.id &&
        c.countryid === selectedCountry
    );

    if (existingDocumentSet && Array.isArray(existingDocumentSet.documents)) {
      return existingDocumentSet.documents.includes(document.id);
    }
    return false;
  };

  const handleToggle = (documentId) => () => {
    setNewDocument((prev) => {
      const existingDocumentSet = prev.find(
        (c) =>
          c.documentset === value &&
          c.courseid === selectedCourse?.id &&
          c.countryid === selectedCountry
      );

      if (existingDocumentSet) {
        const documentExists =
          existingDocumentSet.documents.includes(documentId);

        return prev.map((c) =>
          c.documentset === value &&
          c.courseid === selectedCourse?.id &&
          c.countryid === selectedCountry
            ? {
                ...c,
                documents: documentExists
                  ? c.documents.filter((docId) => docId !== documentId)
                  : [...c.documents, documentId],
              }
            : c
        );
      } else {
        return [
          ...prev,
          {
            countryid: selectedCountry,
            courseid: selectedCourse?.id,
            documentset: value,
            documents: [documentId],
          },
        ];
      }
    });
  };
  return (
    <>
      {Object.keys(selectedCourse).length === 0 ? (
        <Box
          flex={1}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Typography>Please choose a course to add documents</Typography>
        </Box>
      ) : (
        <>
          <Stack
            py={1}
            px={2}
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            {isLoading ? (
              <Skeleton
                variant="rounded"
                sx={{ mt: 3 }}
                width={180}
                height={24}
              />
            ) : (
              <Typography fontWeight={500} fontSize={20} color="#222222">
                Document Options
              </Typography>
            )}
          </Stack>
          <Box px={2}>
            {isLoading ? (
              <Skeleton
                variant="rounded"
                sx={{ my: 1 }}
                width={380}
                height={24}
              />
            ) : (
              <Typography color="textDisabled" fontSize={14}>
                Select the documents which is required for this course
              </Typography>
            )}
            {isLoading ? (
              <Skeleton
                variant="rounded"
                sx={{ mt: 3, mb: 1 }}
                width={"100%"}
                height={40}
              />
            ) : (
              <TextField
                id="outlined-start-adornment"
                placeholder="Search"
                fullWidth
                className="inputMdRounded"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{ bgcolor: "transparent" }}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <ContentPasteSearchOutlined />
                      </InputAdornment>
                    ),
                  },
                }}
              />
            )}
          </Box>
          <List dense sx={{ width: "100%", flex: 1, px: 3, overflowY: "auto" }}>
            {isLoading
              ? Array.from({ length: 7 }).map((_, index) => (
                  <Skeleton
                    key={index}
                    variant="rounded"
                    sx={{ mt: 1 }}
                    width={"100%"}
                    height={32}
                  />
                ))
              : filteredDocuments?.map((item) => (
                  <ListItem
                    key={item.id}
                    secondaryAction={
                      <Checkbox
                        edge="end"
                        onChange={handleToggle(item.id)}
                        checked={isChecked(item)}
                        sx={{
                          display: isChecked(item) ? "inline-flex" : "none",
                        }}
                      />
                    }
                    disablePadding
                    sx={{
                      bgcolor: isChecked(item) ? "#E4EBF4" : "transparent",
                      "&:hover": {
                        "& .MuiCheckbox-root": {
                          display: "inline-flex",
                        },
                      },
                    }}
                  >
                    <ListItemText
                      sx={{ py: 1, px: 2 }}
                      primary={item.documentname}
                    />
                  </ListItem>
                ))}
          </List>{" "}
        </>
      )}
    </>
  );
};
